
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthContext } from '../../context/AuthContext';
import { useWS } from '../../hooks/useWS';
import { API } from '../../utils/api';
import { Layout } from '../../components/Layout';
import { PayerHome } from './PayerHome';
import { ParentHistory } from './ParentHistory';
import { ParentSettings } from './ParentSettings';
import { ParentProfile } from './ParentProfile';
import { P2PContacts } from './P2PContacts';
import { Tools } from '../Tools';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trash2, Clock } from 'lucide-react';

export function ParentDashboard() {
  const { headers, user } = useAuthContext();
  const { status, messages } = useWS(user);
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [analytics, setAnalytics] = useState({ today: { total: 0, count: 0 }, month: { total: 0, count: 0, name: '' }, pending_today: 0 });
  
  // Scheduled payments state
  const [scheduledPayments, setScheduledPayments] = useState([]);
  const [scheduledDialogOpen, setScheduledDialogOpen] = useState(false);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/parent/requests`, { headers, timeout: 30000 });
      setRequests(res.data?.items || []);
    } catch (e) {
      console.error('Failed to fetch requests:', e.message);
    }
  };
  
  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/parent/analytics`, { headers });
      setAnalytics(res.data);
    } catch (e) {
      console.error('Failed to fetch analytics:', e);
    }
  };
  
  const fetchScheduledPayments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scheduled-payment?sort_by=nearest`, { headers });
      setScheduledPayments(res.data?.items || []);
    } catch (e) {
      console.error('Failed to fetch scheduled payments:', e);
    }
  }, [headers]);

  useEffect(() => { 
    if (headers.Authorization && headers.Authorization !== 'Bearer ') {
      fetchRequests(); 
      fetchAnalytics();
      fetchScheduledPayments();
    }
  }, [headers.Authorization, fetchScheduledPayments]);
  
  useEffect(() => {
    const latest = messages[messages.length - 1]; if (!latest) return;
    if (latest.type === 'request_created') { toast.info('New payment request received'); fetchRequests(); fetchAnalytics(); }
    if (latest.type === 'payment_created') {
      const mode = latest.mode || null; const deeplink = latest.deeplink || null; const token = latest.token || null;
      openSwishImmediate(mode, token, deeplink); fetchRequests(); fetchAnalytics();
    }
    if (latest.type === 'payment_update') { toast.success(`Payment ${latest.status}`); fetchRequests(); fetchAnalytics(); }
  }, [messages]);

  const processRequest = async (id) => {
    const res = await axios.post(`${API}/parent/requests/process`, { request_id: id }, { headers });
    console.log('Process response:', res.data);
    const mode = res.data.mode || null; const deeplink = res.data.deeplink || null; const token = res.data.paymentRequestToken || null;
    console.log('Extracted data:', { mode, deeplink, token: token ? token.substring(0, 20) + '...' : null });
    openSwishImmediate(mode, token, deeplink);
  };
  const declineRequest = async (id) => { await axios.post(`${API}/parent/requests/decline`, { request_id: id }, { headers }); toast.message('Declined'); fetchRequests(); };
  const markPaid = async (id) => { try { await axios.post(`${API}/parent/requests/mark-paid`, { request_id: id }, { headers }); toast.success('Marked as paid'); fetchRequests(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark as paid'); } };

  // Handle scheduled payment actions
  const handleProcessScheduled = (payment) => {
    const data = {
      version: 1,
      payee: { value: payment.recipient?.alias || payment.recipient?.name, editable: !payment.recipient?.alias },
      amount: { value: payment.amount, editable: false },
      message: { value: payment.description || '', editable: true }
    };
    const encoded = encodeURIComponent(JSON.stringify(data));
    window.location.href = `swish://payment?data=${encoded}`;
  };

  const handleCancelScheduled = async (paymentId) => {
    try {
      await axios.delete(`${API}/scheduled-payment/${paymentId}`, { headers });
      toast.success('Scheduled payment cancelled');
      fetchScheduledPayments();
    } catch (e) {
      toast.error('Failed to cancel');
    }
  };

  const formatDate = (isoString) => {
    try {
      return new Date(isoString).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const openSwishImmediate = (mode, token, deeplink) => {
    console.log('============ openSwishImmediate DEBUG (V3.1) ============');
    console.log('mode:', mode);
    console.log('token:', token);
    console.log('deeplink:', deeplink);
    console.log('==========================================================');
    
    // V3.1: Handle new payment modes
    
    // APP2APP mode - pre-filled payment with callback (new in V3.1)
    if (mode === 'APP2APP') {
      if (deeplink) {
        console.log('[APP2APP mode] Opening deeplink:', deeplink);
        window.location.href = deeplink;
        return;
      }
      console.error('[ERROR] APP2APP mode but no deeplink provided');
      return;
    }
    
    // TOKENIZED mode - merchant API token with App2App callback (new in V3.1)
    if (mode === 'TOKENIZED') {
      if (deeplink) {
        console.log('[TOKENIZED mode] Opening deeplink with token:', deeplink);
        window.location.href = deeplink;
        return;
      }
      // Fallback: construct deeplink from token
      if (token) {
        const callbackurl = `${window.location.origin}/payment-return`;
        const link = `swish://paymentrequest?token=${token}&callbackurl=${encodeURIComponent(callbackurl)}`;
        console.log('[TOKENIZED mode fallback] Opening:', link);
        window.location.href = link;
        return;
      }
      console.error('[ERROR] TOKENIZED mode but no deeplink or token');
      return;
    }
    
    // EXTERNAL mode - pass-through
    if (mode === 'EXTERNAL') {
      if (deeplink && deeplink.startsWith('swish://')) { 
        console.log('[EXTERNAL mode] Opening deeplink:', deeplink);
        window.location.href = deeplink; 
        return; 
      }
      if (token) { 
        const link = `swish://paymentrequest?token=${encodeURIComponent(token)}`;
        console.log('[EXTERNAL with token] Opening:', link);
        window.location.href = link; 
        return;
      }
      console.error('[ERROR] EXTERNAL mode but no deeplink or token');
      return;
    }
    
    // Legacy INTERNAL mode (for backward compatibility)
    if (mode === 'INTERNAL' || !mode) {
      if (token) { 
        const callbackurl = `${window.location.origin}/payment-return`;
        const link = `swish://paymentrequest?token=${token}&callbackurl=${encodeURIComponent(callbackurl)}`;
        console.log('[INTERNAL mode] Opening:', link);
        window.location.href = link; 
        return;
      }
      console.error('[ERROR] INTERNAL mode but no token');
    }
    
    // Legacy STATIC mode
    if (mode === 'STATIC') {
      if (deeplink) {
        console.log('[STATIC mode] Opening deeplink:', deeplink);
        window.location.href = deeplink;
        return;
      }
      console.error('[ERROR] STATIC mode but no deeplink');
    }
  };

  return (
    <>
      <Layout 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        role="parent"
        scheduledCount={scheduledPayments.length}
        onScheduledClick={() => setScheduledDialogOpen(true)}
      >
        {activeTab === 'home' && <PayerHome />}
        {activeTab === 'contacts' && <P2PContacts />}
        {activeTab === 'history' && <ParentHistory />}
        {activeTab === 'tools' && <Tools />}
        {activeTab === 'settings' && <ParentSettings headers={headers} user={user} />}
        {activeTab === 'profile' && <ParentProfile user={user} headers={headers} />}
      </Layout>
      
      {/* Scheduled Payments Dialog */}
      <Dialog open={scheduledDialogOpen} onOpenChange={setScheduledDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Scheduled Payments
            </DialogTitle>
            <DialogDescription>
              Upcoming payments scheduled for later
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {scheduledPayments.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No scheduled payments</p>
            ) : (
              scheduledPayments.map((payment) => (
                <div 
                  key={payment._id} 
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    payment.is_overdue ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'
                  }`}
                  data-testid={`scheduled-payment-${payment._id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-lg">{payment.amount} SEK</p>
                    <p className="text-sm text-muted-foreground truncate">
                      To: {payment.recipient?.name}
                    </p>
                    {payment.description && (
                      <p className="text-xs text-muted-foreground truncate mt-1">
                        {payment.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {payment.is_overdue && <span className="text-orange-600 font-medium">⚠️ Overdue • </span>}
                      {formatDate(payment.scheduled_at)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 ml-3">
                    <Button size="sm" onClick={() => handleProcessScheduled(payment)} data-testid={`scheduled-pay-${payment._id}`}>
                      Pay Now
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleCancelScheduled(payment._id)} data-testid={`scheduled-cancel-${payment._id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
